<template>
  <div class="pp-panel">
    <div v-if="!hasContent" class="pp-empty">
      <span class="pp-empty-text">Podcast script not generated yet.</span>
    </div>

    <template v-else>
      <div class="pp-meta">
        <h3 class="pp-title">{{ pod.episode_title }}</h3>
        <div v-if="pod.episode_subtitle" class="pp-subtitle">{{ pod.episode_subtitle }}</div>
        <div class="pp-meta-row">
          <span class="pp-chip">~{{ totalMin }} min</span>
          <span v-if="editorSec" class="pp-chip pp-chip-muted">Editor's note ~{{ editorSec }}s</span>
          <span v-if="mainSec" class="pp-chip pp-chip-muted">Main ~{{ mainSec }}s</span>
          <span v-for="tag in (pod.tags || [])" :key="tag" class="pp-tag">{{ tag }}</span>
        </div>
      </div>

      <div class="pp-section">
        <div class="pp-section-label">Voice assignments</div>
        <div v-if="!editingVoices" class="pp-voices-row">
          <div class="pp-voice-cell">
            <span class="pp-voice-label">Editor (cloned)</span>
            <span :class="['pp-voice-val', editorVoiceSet ? '' : 'pp-voice-empty']">
              {{ editorVoiceSet || 'Not set' }}
            </span>
          </div>
          <div class="pp-voice-cell">
            <span class="pp-voice-label">Narrator (AI)</span>
            <span :class="['pp-voice-val', narratorVoiceSet ? '' : 'pp-voice-empty']">
              {{ narratorVoiceSet || 'Not set' }}
            </span>
          </div>
          <el-button size="mini" type="text" icon="el-icon-edit" @click="startEdit">Edit</el-button>
        </div>
        <div v-else class="pp-voices-edit">
          <el-input v-model="editorDraft" size="small" placeholder="ElevenLabs voice_id of cloned editor" class="pp-voice-input" />
          <el-input v-model="narratorDraft" size="small" placeholder="ElevenLabs voice_id of AI narrator" class="pp-voice-input" />
          <div class="pp-voice-actions">
            <el-button size="mini" :loading="saving" type="primary" @click="saveVoices" :disabled="!canSave">Save</el-button>
            <el-button size="mini" @click="cancelEdit" :disabled="saving">Cancel</el-button>
          </div>
          <div v-if="editorDraft && narratorDraft && editorDraft === narratorDraft" class="pp-voice-warn">
            Editor and narrator must be different voices.
          </div>
        </div>
      </div>

      <div v-if="pod.ai_disclosure_phrase" class="pp-section">
        <div class="pp-section-label">AI disclosure</div>
        <div class="pp-disclosure">{{ pod.ai_disclosure_phrase }}</div>
      </div>

      <div v-if="pod.editor_note" class="pp-section">
        <div class="pp-section-label">
          Editor's note
          <span class="pp-section-meta">{{ pod.editor_note.word_count }} words · ~{{ editorSec }}s</span>
        </div>
        <pre class="pp-script">{{ pod.editor_note.content_md }}</pre>
        <el-button size="mini" type="text" icon="el-icon-document-copy" @click="copy(pod.editor_note.content_md, 'Editor\'s note')">Copy</el-button>
      </div>

      <div v-if="pod.main_content" class="pp-section">
        <div class="pp-section-label">
          Main content
          <span class="pp-section-meta">{{ pod.main_content.word_count }} words · ~{{ mainSec }}s</span>
        </div>
        <pre class="pp-script pp-script-tall">{{ pod.main_content.content_md }}</pre>
        <el-button size="mini" type="text" icon="el-icon-document-copy" @click="copy(pod.main_content.content_md, 'Main content')">Copy</el-button>
      </div>

      <div v-if="pod.show_notes_md" class="pp-section">
        <div class="pp-section-label">Show notes</div>
        <pre class="pp-script pp-script-short">{{ pod.show_notes_md }}</pre>
        <el-button size="mini" type="text" icon="el-icon-document-copy" @click="copy(pod.show_notes_md, 'Show notes')">Copy</el-button>
      </div>

      <div v-if="pod.transition_cue" class="pp-section pp-cue-row">
        <div class="pp-section-label">Transition cue</div>
        <span class="pp-cue-badge">{{ pod.transition_cue.type || 'silence' }} · {{ pod.transition_cue.duration_ms || 800 }}ms</span>
      </div>
    </template>
  </div>
</template>

<script>
import { postRequest } from "@/apis/front";
export default {
  name: "PodcastScriptPanel",
  props: {
    collectionForm: { type: Object, required: true },
    collectionId: { type: String, default: "" },
  },
  data() {
    return {
      editingVoices: false,
      editorDraft: "",
      narratorDraft: "",
      saving: false,
    };
  },
  computed: {
    pod() {
      return this.collectionForm && this.collectionForm.podcast_script
        ? this.collectionForm.podcast_script
        : null;
    },
    hasContent() {
      return !!(this.pod && (this.pod.episode_title || this.pod.main_content));
    },
    totalMin() {
      if (!this.pod || !this.pod.total_estimated_seconds) return 0;
      return Math.round(this.pod.total_estimated_seconds / 60);
    },
    editorSec() {
      return this.pod && this.pod.editor_note ? this.pod.editor_note.estimated_seconds : 0;
    },
    mainSec() {
      return this.pod && this.pod.main_content ? this.pod.main_content.estimated_seconds : 0;
    },
    editorVoiceSet() {
      return this.pod && this.pod.voice_assignments ? this.pod.voice_assignments.editor : "";
    },
    narratorVoiceSet() {
      return this.pod && this.pod.voice_assignments ? this.pod.voice_assignments.narrator : "";
    },
    canSave() {
      return !!this.editorDraft && !!this.narratorDraft && this.editorDraft !== this.narratorDraft;
    },
  },
  methods: {
    startEdit() {
      this.editorDraft = this.editorVoiceSet || "";
      this.narratorDraft = this.narratorVoiceSet || "";
      this.editingVoices = true;
    },
    cancelEdit() {
      this.editorDraft = "";
      this.narratorDraft = "";
      this.editingVoices = false;
    },
    async saveVoices() {
      if (!this.canSave) return;
      if (!this.collectionId) {
        this.$message.error("No collection_id available; save the collection first.");
        return;
      }
      this.saving = true;
      try {
        const updated = {
          ...this.pod,
          voice_assignments: {
            editor: this.editorDraft,
            narrator: this.narratorDraft,
          },
        };
        const response = await postRequest("repairPostCollection", {
          id: this.collectionId,
          podcast_script: updated,
        });
        if (response.data && response.data.code === 0) {
          this.$emit("update", updated);
          this.$message.success("Voice assignments saved");
          this.editingVoices = false;
        } else {
          this.$message.error((response.data && response.data.msg) || "Save failed");
        }
      } catch (e) {
        this.$message.error("Save failed: " + (e.message || e));
      } finally {
        this.saving = false;
      }
    },
    copy(text, label) {
      if (!text) return;
      const fallback = () => {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand("copy"); } catch (e) {}
        document.body.removeChild(ta);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
          .then(() => this.$message.success(`${label} copied`))
          .catch(() => { fallback(); this.$message.success(`${label} copied`); });
      } else {
        fallback();
        this.$message.success(`${label} copied`);
      }
    },
  },
};
</script>

<style scoped>
.pp-panel { padding: 10px 4px; }
.pp-empty { padding: 24px; text-align: center; color: #909399; font-style: italic; }
.pp-meta { padding: 6px 0 14px; border-bottom: 1px solid #f0f2f5; }
.pp-title { margin: 0 0 4px; font-size: 20px; font-weight: 600; color: #303133; line-height: 1.3; }
.pp-subtitle { font-size: 14px; color: #606266; margin-bottom: 8px; }
.pp-meta-row { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.pp-chip { display: inline-block; padding: 2px 8px; background: #fdf6ec; color: #e6a23c; border-radius: 3px; font-size: 12px; }
.pp-chip-muted { background: #f4f4f5; color: #909399; }
.pp-tag { display: inline-block; padding: 2px 8px; background: #ecf5ff; color: #409eff; border-radius: 3px; font-size: 12px; }
.pp-section { padding: 10px 0; border-bottom: 1px solid #f0f2f5; }
.pp-section:last-of-type { border-bottom: none; }
.pp-section-label { font-size: 12px; color: #909399; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 6px; display: flex; gap: 12px; align-items: baseline; }
.pp-section-meta { font-size: 11px; color: #c0c4cc; text-transform: none; letter-spacing: 0; }
.pp-disclosure { font-size: 13px; color: #606266; font-style: italic; padding: 6px 10px; background: #f4f4f5; border-radius: 3px; }
.pp-script { background: #fafafa; border: 1px solid #e4e7ed; border-radius: 4px; padding: 12px 14px; font-family: -apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,sans-serif; font-size: 13px; line-height: 1.6; color: #303133; white-space: pre-wrap; word-break: normal; max-height: 220px; overflow-y: auto; margin: 0 0 6px; }
.pp-script-tall { max-height: 360px; }
.pp-script-short { max-height: 160px; }
.pp-voices-row { display: flex; gap: 24px; align-items: center; padding: 6px 0; }
.pp-voice-cell { display: flex; flex-direction: column; gap: 2px; }
.pp-voice-label { font-size: 11px; color: #909399; text-transform: uppercase; letter-spacing: 0.05em; }
.pp-voice-val { font-family: SFMono-Regular, Menlo, monospace; font-size: 12px; color: #303133; }
.pp-voice-empty { color: #c0c4cc; font-style: italic; }
.pp-voices-edit { display: flex; flex-direction: column; gap: 8px; padding: 6px 0; max-width: 540px; }
.pp-voice-input { width: 100%; }
.pp-voice-actions { display: flex; gap: 8px; }
.pp-voice-warn { font-size: 12px; color: #f56c6c; padding: 4px 0; }
.pp-cue-row { display: flex; gap: 16px; align-items: center; }
.pp-cue-row .pp-section-label { margin-bottom: 0; flex: 0 0 auto; }
.pp-cue-badge { display: inline-block; padding: 2px 8px; background: #f4f4f5; color: #606266; border-radius: 3px; font-size: 12px; font-family: SFMono-Regular, Menlo, monospace; }
</style>
